import type { ISODateTimeString, UUID } from './shared';

export type InteractionType = (typeof InteractionType)[keyof typeof InteractionType];

export const InteractionType = {
  uplift: 'uplift',
  me_too: 'me_too',
  i_can_help: 'i_can_help',
  bookmark: 'bookmark',
  view: 'view',
  we_met: 'we_met',
  remember: 'remember',
  added_note: 'added_note',
} as const;

export type InteractionTarget = (typeof InteractionTarget)[keyof typeof InteractionTarget];

export const InteractionTarget = {
  question: 'question',
  answer: 'answer',
  event: 'event',
  user: 'user',
} as const;

export interface InteractionCreate {
  user_id?: UUID | null;
  target_type: InteractionTarget;
  target_id: UUID;
  interaction_type: InteractionType;
  content?: string | null;
  event_id?: UUID | null;
  is_anonymous?: boolean;
}

export interface InteractionRead {
  interaction_type: InteractionType;
  target_type: InteractionTarget;
  id: UUID;
  user_id: UUID;
  target_id: UUID;
  content: string | null;
  event_id: UUID | null;
  is_anonymous: boolean;
  is_revealed: boolean;
  created_at: ISODateTimeString;
}

export interface InteractionToggleResponse {
  interaction: InteractionRead | null;
  toggled_on: boolean;
  message: string;
}

export interface Author {
  id: UUID;
  name: string;
  profile_picture: string | null;
  slug: string | null;
}

export interface Creator {
  id: UUID;
  name: string;
  profile_picture: string | null;
  slug: string | null;
}

export interface EventDetail {
  id: UUID | null;
  name: string;
  image_url: string | null;
}

// AIDEV-NOTE(TECH-84): Hand-added ahead of regeneration. Backend ships this on
// origin/dev (`app/schemas/interaction.py:115`, merge 0a93e4dd) but the
// generator hasn't been re-run. Remove this comment after the next regen.
export interface SpaceDetail {
  id: UUID | null;
  name: string;
  image_url: string | null;
}

export interface QuestionDetail {
  id: UUID;
  title: string;
  body: string;
  author: Author;
  event: EventDetail | null;
}

export interface AnswerDetail {
  id: UUID;
  content: string;
  author: Author;
}

export interface UserDetail {
  id: UUID;
  name: string;
  profile_picture: string | null;
  slug: string;
}

/**
 * Common fields every InteractionItem variant carries. The variant types
 * below extend this and pin down which `on` + `type` combination is valid,
 * which fields must be present, and which must be null.
 */
export interface InteractionItemBase {
  id: UUID;
  created_at: ISODateTimeString;
  content: string | null;
  is_anonymous: boolean;
  is_revealed: boolean;
}

/**
 * `interaction_type === 'me_too' | 'uplift' | 'bookmark' | 'i_can_help' | 'view'`
 * on a question. `question` is always populated. `answer` is loose because
 * `i_can_help` carries the offered-help answer alongside the question
 * (backend ships both for that interaction type).
 */
export interface QuestionTargetInteractionItem extends InteractionItemBase {
  type: InteractionType;
  on: 'question';
  question: QuestionDetail;
  answer: AnswerDetail | string | null;
  event: null;
  space: null;
  user: null;
}

/**
 * Same `interaction_type` family as `QuestionTargetInteractionItem`, but
 * recorded against an answer rather than the parent question. `answer` is
 * always populated. `question` may also be populated as parent context.
 */
export interface AnswerTargetInteractionItem extends InteractionItemBase {
  type: InteractionType;
  on: 'answer';
  question: QuestionDetail | null;
  answer: AnswerDetail | string;
  event: null;
  space: null;
  user: null;
}

/**
 * Interactions recorded against an event (e.g. a view). `event` is always
 * populated.
 */
export interface EventTargetInteractionItem extends InteractionItemBase {
  type: InteractionType;
  on: 'event';
  question: null;
  answer: null;
  event: EventDetail;
  space: null;
  user: null;
}

/**
 * Common fields for `on: 'user'` interactions. `we_met` requires exactly
 * one of `event`/`space` (see the two sub-variants below); other user
 * interactions (`remember`) have neither.
 */
interface UserTargetBase extends InteractionItemBase {
  on: 'user';
  user: UserDetail;
  question: null;
  answer: null;
}

/**
 * `we_met` recorded against an event. Backend ships `event` populated and
 * `space` null. The XOR is enforced here so the consumer can't read both.
 */
export interface WeMetEventInteractionItem extends UserTargetBase {
  type: 'we_met';
  event: EventDetail;
  space: null;
}

/**
 * `we_met` recorded against a space. AIDEV-NOTE(TECH-84): the `space`
 * field was hand-added ahead of generator re-run — remove this note after
 * regeneration.
 */
export interface WeMetSpaceInteractionItem extends UserTargetBase {
  type: 'we_met';
  event: null;
  space: SpaceDetail;
}

/**
 * Any non-we_met user-target interaction (currently `remember`). Neither
 * event nor space applies.
 */
export interface OtherUserTargetInteractionItem extends UserTargetBase {
  type: Exclude<InteractionType, 'we_met'>;
  event: null;
  space: null;
}

export type UserTargetInteractionItem =
  WeMetEventInteractionItem | WeMetSpaceInteractionItem | OtherUserTargetInteractionItem;

/**
 * Discriminated union over `on` (target_type), with `we_met` further
 * refined by event-XOR-space. The union makes impossible combos
 * (`{on: 'question', question: null}`, `{on: 'user', type: 'we_met',
 * event: null, space: null}`, etc.) into compile errors.
 */
export type InteractionItem =
  | QuestionTargetInteractionItem
  | AnswerTargetInteractionItem
  | EventTargetInteractionItem
  | UserTargetInteractionItem;

export interface InteractionPagination {
  page: number;
  per_page: number;
  total_pages: number;
  items_in_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

export interface InteractionsResponse {
  total: number;
  most_recent_created_at: ISODateTimeString | null;
  pagination: InteractionPagination;
  list: InteractionItem[];
}
